import { create } from 'zustand';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { ConversationWithDetails } from '@/stores/messageStore';

interface ReviewUserSummary {
  userId: string;
  displayName: string;
  iconUrl?: string;
}

export interface ReviewRecord {
  reviewId: string;
  fromUserId: string;
  toUserId: string;
  conversationId: string;
  rating: 1 | 2 | 3 | 4 | 5;
  comment?: string;
  createdAt: Date;
  updatedAt: Date;
  fromUser?: ReviewUserSummary;
  toUser?: ReviewUserSummary;
}

export interface ReviewStatistics {
  averageRating: number;
  totalCount: number;
  distribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
}

export interface ReviewableConversation {
  conversationId: string;
  otherUserId: string;
  displayName: string;
  iconUrl?: string;
  lastMessageAt?: Date;
}

interface ReviewState {
  receivedReviews: ReviewRecord[];
  sentReviews: ReviewRecord[];
  reviewableConversations: ReviewableConversation[];
  receivedStats: ReviewStatistics;
  isLoadingReceived: boolean;
  isLoadingSent: boolean;
  isLoadingReviewable: boolean;
  isSubmitting: boolean;
  error: string | null;

  fetchReceivedReviews: (toUserId: string) => Promise<void>;
  fetchSentReviews: (fromUserId: string) => Promise<void>;
  fetchReviewableConversations: (
    fromUserId: string,
    conversations: ConversationWithDetails[]
  ) => Promise<void>;
  submitReview: (params: {
    fromUserId: string;
    toUserId: string;
    conversationId: string;
    rating: 1 | 2 | 3 | 4 | 5;
    comment?: string;
  }) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

const EMPTY_STATS: ReviewStatistics = {
  averageRating: 0,
  totalCount: 0,
  distribution: {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  },
};

function toDate(value: unknown): Date {
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate: unknown }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
}

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Date) {
    return value;
  }
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate: unknown }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return undefined;
}

function toRating(value: unknown): 1 | 2 | 3 | 4 | 5 {
  if (value === 1 || value === 2 || value === 3 || value === 4 || value === 5) {
    return value;
  }
  return 5;
}

function buildStats(reviews: ReviewRecord[]): ReviewStatistics {
  if (reviews.length === 0) {
    return EMPTY_STATS;
  }

  const distribution: ReviewStatistics['distribution'] = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  };

  let total = 0;
  reviews.forEach((review) => {
    distribution[review.rating] += 1;
    total += review.rating;
  });

  const averageRating = Math.round((total / reviews.length) * 10) / 10;
  return {
    averageRating,
    totalCount: reviews.length,
    distribution,
  };
}

async function fetchUsers(userIds: string[]): Promise<Map<string, ReviewUserSummary>> {
  const uniqueUserIds = Array.from(new Set(userIds)).filter((userId) => userId.length > 0);
  if (uniqueUserIds.length === 0) {
    return new Map();
  }

  const userPairs = await Promise.all(
    uniqueUserIds.map(async (userId) => {
      const userSnap = await getDoc(doc(db, 'users', userId));
      if (!userSnap.exists()) {
        return [
          userId,
          {
            userId,
            displayName: '不明なユーザー',
          } satisfies ReviewUserSummary,
        ] as const;
      }

      const userData = userSnap.data() as { displayName?: unknown; iconUrl?: unknown };
      return [
        userId,
        {
          userId,
          displayName:
            typeof userData.displayName === 'string' && userData.displayName
              ? userData.displayName
              : '不明なユーザー',
          iconUrl: typeof userData.iconUrl === 'string' ? userData.iconUrl : undefined,
        } satisfies ReviewUserSummary,
      ] as const;
    })
  );

  return new Map(userPairs);
}

function sortByCreatedAtDesc<T extends { createdAt: Date }>(items: T[]): T[] {
  return [...items].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

export const useReviewStore = create<ReviewState>((set) => ({
  receivedReviews: [],
  sentReviews: [],
  reviewableConversations: [],
  receivedStats: EMPTY_STATS,
  isLoadingReceived: false,
  isLoadingSent: false,
  isLoadingReviewable: false,
  isSubmitting: false,
  error: null,

  fetchReceivedReviews: async (toUserId) => {
    try {
      set({ isLoadingReceived: true, error: null });
      const reviewsRef = collection(db, 'reviews');
      const reviewsQuery = query(
        reviewsRef,
        where('toUserId', '==', toUserId),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
      const snapshot = await getDocs(reviewsQuery);

      const rawReviews: ReviewRecord[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data() as Record<string, unknown>;
        return {
          reviewId: docSnap.id,
          fromUserId: typeof data.fromUserId === 'string' ? data.fromUserId : '',
          toUserId: typeof data.toUserId === 'string' ? data.toUserId : '',
          conversationId: typeof data.conversationId === 'string' ? data.conversationId : '',
          rating: toRating(data.rating),
          comment: typeof data.comment === 'string' ? data.comment : undefined,
          createdAt: toDate(data.createdAt),
          updatedAt: toDate(data.updatedAt),
        };
      });

      const fromUsers = await fetchUsers(rawReviews.map((review) => review.fromUserId));
      const nextReviews = rawReviews.map((review) => ({
        ...review,
        fromUser: fromUsers.get(review.fromUserId),
      }));

      set({
        receivedReviews: nextReviews,
        receivedStats: buildStats(nextReviews),
        isLoadingReceived: false,
      });
    } catch (error) {
      console.error('Error fetching received reviews:', error);
      set({
        error: 'レビューの取得に失敗しました',
        isLoadingReceived: false,
      });
    }
  },

  fetchSentReviews: async (fromUserId) => {
    try {
      set({ isLoadingSent: true, error: null });
      const reviewsRef = collection(db, 'reviews');
      const reviewsQuery = query(
        reviewsRef,
        where('fromUserId', '==', fromUserId),
        limit(100)
      );
      const snapshot = await getDocs(reviewsQuery);

      const rawReviews: ReviewRecord[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data() as Record<string, unknown>;
        return {
          reviewId: docSnap.id,
          fromUserId: typeof data.fromUserId === 'string' ? data.fromUserId : '',
          toUserId: typeof data.toUserId === 'string' ? data.toUserId : '',
          conversationId: typeof data.conversationId === 'string' ? data.conversationId : '',
          rating: toRating(data.rating),
          comment: typeof data.comment === 'string' ? data.comment : undefined,
          createdAt: toDate(data.createdAt),
          updatedAt: toDate(data.updatedAt),
        };
      });

      const toUsers = await fetchUsers(rawReviews.map((review) => review.toUserId));
      const withUsers = rawReviews.map((review) => ({
        ...review,
        toUser: toUsers.get(review.toUserId),
      }));

      set({
        sentReviews: sortByCreatedAtDesc(withUsers),
        isLoadingSent: false,
      });
    } catch (error) {
      console.error('Error fetching sent reviews:', error);
      set({
        error: '投稿済みレビューの取得に失敗しました',
        isLoadingSent: false,
      });
    }
  },

  fetchReviewableConversations: async (fromUserId, conversations) => {
    try {
      set({ isLoadingReviewable: true, error: null });

      const reviewsRef = collection(db, 'reviews');
      const sentReviewsQuery = query(
        reviewsRef,
        where('fromUserId', '==', fromUserId),
        limit(200)
      );
      const sentReviewsSnap = await getDocs(sentReviewsQuery);
      const reviewedConversationIds = new Set(
        sentReviewsSnap.docs
          .map((docSnap) => docSnap.data() as { conversationId?: unknown })
          .map((data) => (typeof data.conversationId === 'string' ? data.conversationId : ''))
          .filter((conversationId) => conversationId.length > 0)
      );

      const candidates = conversations
        .filter((conversation) => !reviewedConversationIds.has(conversation.id))
        .map((conversation) => ({
          conversationId: conversation.id,
          otherUserId: conversation.participant.id,
          displayName: conversation.participant.displayName,
          iconUrl: conversation.participant.iconUrl,
          lastMessageAt: toOptionalDate(conversation.lastMessageAt),
        }))
        .filter((candidate) => candidate.otherUserId.length > 0)
        .sort((a, b) => {
          const aTime = a.lastMessageAt ? a.lastMessageAt.getTime() : 0;
          const bTime = b.lastMessageAt ? b.lastMessageAt.getTime() : 0;
          return bTime - aTime;
        });

      set({
        reviewableConversations: candidates,
        isLoadingReviewable: false,
      });
    } catch (error) {
      console.error('Error fetching reviewable conversations:', error);
      set({
        error: 'レビュー投稿候補の取得に失敗しました',
        isLoadingReviewable: false,
      });
    }
  },

  submitReview: async ({ fromUserId, toUserId, conversationId, rating, comment }) => {
    try {
      set({ isSubmitting: true, error: null });

      const reviewId = `${fromUserId}_${conversationId}`;
      const reviewRef = doc(db, 'reviews', reviewId);
      const existingReview = await getDoc(reviewRef);
      if (existingReview.exists()) {
        throw new Error('already-reviewed');
      }

      await setDoc(reviewRef, {
        fromUserId,
        toUserId,
        conversationId,
        rating,
        comment: comment?.trim() || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      set({ isSubmitting: false });
    } catch (error) {
      console.error('Error submitting review:', error);
      const message =
        error instanceof Error && error.message === 'already-reviewed'
          ? 'この会話のレビューは投稿済みです'
          : 'レビューの投稿に失敗しました';
      set({
        error: message,
        isSubmitting: false,
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  reset: () =>
    set({
      receivedReviews: [],
      sentReviews: [],
      reviewableConversations: [],
      receivedStats: EMPTY_STATS,
      isLoadingReceived: false,
      isLoadingSent: false,
      isLoadingReviewable: false,
      isSubmitting: false,
      error: null,
    }),
}));
